import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { handleTokenAndPostRequest } from "../utils/utils";
import { FaChartBar, FaNewspaper, FaQrcode, FaSearch, FaTimes, FaRegNewspaper, FaRobot, FaBullhorn, FaUserCircle, FaHome, FaBell, FaHistory, FaCog, FaEnvelopeOpenText, FaUser, FaUsers, FaChevronDown, FaChevronUp, FaCloudUploadAlt, FaRocket, FaCreditCard, FaVideo, FaCommentDots, FaMapMarkedAlt, FaAddressBook, FaInbox, FaClipboardCheck } from "react-icons/fa";
import VendorleadsGradientText from "../components/VendorleadsGradientText";
import UploadContactsModal from "../components/UploadContactsModal";
import GradientHouseIcon from '../components/GradientHouseIcon';
import { useViewScope } from '../contexts/ViewScopeContext';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);
  return matches;
}

export default function MainNav() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [hasContacts, setHasContacts] = useState(true);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const sidebarRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Auto-open mobile nav after onboarding completes
  useEffect(() => {
    const openNav = () => {
      if (!isDesktop) {
        setTimeout(() => setMobileNavOpen(true), 400);
      }
    };
    // Check on mount
    if (sessionStorage.getItem("onboarding_just_finished") === "1") {
      sessionStorage.removeItem("onboarding_just_finished");
      openNav();
    }
    // Listen for event dispatched when wizard finishes
    window.addEventListener("onboarding-finished", openNav);
    return () => window.removeEventListener("onboarding-finished", openNav);
  }, [isDesktop]);

  useEffect(() => {
    handleTokenAndPostRequest(`${window.api_host}/authenticate-token-agent`)
      .then(res => {
        const data = res.data;
        const userData = {
          email: data.email,
          name: data.agent_details?.agent_name || data.email,
          agent_details: data.agent_details || {},
        };
        setUser(userData);
        setHasContacts(data.has_contacts !== false);
        localStorage.setItem('user', JSON.stringify(userData));
      })
      .catch(err => {
        if (err.status === 401) {
          localStorage.removeItem("jwt_token");
          localStorage.removeItem("user");
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath && currentPath !== "/") {
            sessionStorage.setItem("redirect_after_login", currentPath);
          }
          navigate("/", { replace: true });
        }
      });
  }, [navigate]);

  const userName = user.name || user.email || 'User';
  const userEmail = user.email || '';

  // Poll the AI review queue so the sidebar shows a live "needs review"
  // count badge. Also listens for a custom event that AIConversations
  // dispatches after a send/dismiss so the badge updates instantly.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      handleTokenAndPostRequest(
        `${window.api_host}/ai-outbound-queue/list`,
        { status: 'pending' },
        { 'Content-Type': 'application/json' },
      )
        .then(res => {
          if (cancelled) return;
          if (res?.data?.status === 'success' && Array.isArray(res.data.items)) {
            setPendingReviewCount(res.data.items.length);
          }
        })
        .catch(() => { /* ignore — keep last known value */ });
    };
    fetchCount();
    const onChanged = (e) => {
      if (e && e.detail && typeof e.detail.count === 'number') {
        setPendingReviewCount(e.detail.count);
      }
    };
    window.addEventListener('needs-review-count-changed', onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('needs-review-count-changed', onChanged);
    };
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    function handleClick(e) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setMobileNavOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileNavOpen]);

  function HamburgerBtn({ onClick, style = {}, ariaLabel = "Open navigation", color = "#fff" }) {
    return (
      <button
        aria-label={ariaLabel}
        onClick={onClick}
        data-event-tab="main_nav"
        data-event-field="menu"
        data-event-action="open"
        style={{
          background: "none",
          border: "none",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
          ...style,
        }}
      >
        <span style={{ display: "block", width: 24, height: 18, position: "relative" }}>
          <span style={{
            position: "absolute", height: 3, width: 24, background: color, borderRadius: 2, top: 0, left: 0
          }} />
          <span style={{
            position: "absolute", height: 3, width: 24, background: color, borderRadius: 2, top: 7.5, left: 0
          }} />
          <span style={{
            position: "absolute", height: 3, width: 24, background: color, borderRadius: 2, top: 15, left: 0
          }} />
        </span>
      </button>
    );
  }

  function CloseBtn({ onClick, style = {}, ariaLabel = "Close navigation", color = "#b2dfdb" }) {
    return (
      <button
        aria-label={ariaLabel}
        onClick={onClick}
        data-event-tab="main_nav"
        data-event-field="menu"
        data-event-action="close"
        style={{
          background: "none",
          border: "none",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
          position: "absolute",
          top: 20,
          right: 8,
          zIndex: 1001,
          ...style,
        }}
      >
        <FaTimes color={color} size={20} />
      </button>
    );
  }

  // Use 100dvh on mobile so the sidebar respects the browser's dynamic UI
  // (URL bar / bottom toolbar). 100vh on real mobile devices includes the
  // area behind the browser chrome, which causes bottom nav items
  // (e.g. "Send Feedback") to be hidden under the URL bar.
  const sidebarStyle = {
    width: isDesktop ? 260 : "80vw",
    background: "linear-gradient(180deg, #0f3c3f 0%, #17403b 100%)",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    left: 0,
    top: 0,
    height: "100vh", // fallback
    maxHeight: "100vh",
    minHeight: 0,
    zIndex: 1200,
    boxShadow: isDesktop ? "2px 0 8px #0002" : "none",
    transition: "transform 0.3s ease",
    overflow: "hidden",
    ...(isDesktop
      ? {}
      : {
          width: "80vw",
          maxWidth: "80vw",
          height: "100dvh",
          maxHeight: "100dvh",
          transform: mobileNavOpen ? "translateX(0)" : "translateX(-100%)",
        }),
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100dvh",
    background: "rgba(0,0,0,0.35)",
    zIndex: 1100,
    opacity: mobileNavOpen ? 1 : 0,
    pointerEvents: mobileNavOpen ? "auto" : "none",
    transition: "opacity 0.25s ease",
  };

  function ScopeToggle() {
    const { viewScope, setViewScope, agentFilter, setAgentFilter, team, mySlug, teamLoading } = useViewScope();
    const [dropOpen, setDropOpen] = useState(false);
    const dropRef = useRef();

    useEffect(() => {
      function handleClick(e) {
        if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      }
      if (dropOpen) document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }, [dropOpen]);

    if (teamLoading || !team || team.length <= 1) return null;

    const otherAgents = team.filter((t) => t.landing_page_slug !== mySlug);
    const selectedAgent = agentFilter ? team.find((t) => t.landing_page_slug === agentFilter) : null;

    let label = "My Leads";
    if (viewScope === "agency" && selectedAgent) label = selectedAgent.agent_name || selectedAgent.agent_email;
    else if (viewScope === "agency") label = "All Team";

    return (
      <div style={{ position: "relative" }} ref={dropRef}>
        <div style={{ fontSize: '11px', color: "#b2dfdb", marginBottom: 6 }}>
          Viewing
        </div>
        <button
          onClick={() => setDropOpen((v) => !v)}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #2a5e55",
            background: "#0f3c3f",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {viewScope === "mine"
            ? <FaUser size={11} style={{ color: "#10b981", flexShrink: 0 }} />
            : <FaUsers size={12} style={{ color: "#10b981", flexShrink: 0 }} />
          }
          <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {label}
          </span>
          <FaChevronDown size={9} style={{ color: "#7fb8b0", flexShrink: 0 }} />
        </button>
        {dropOpen && (
          <div style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            marginBottom: 4,
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            zIndex: 1300,
            overflow: "hidden",
            maxHeight: 240,
            overflowY: "auto",
          }}>
            <button
              onClick={() => { setViewScope("mine"); setAgentFilter(null); setDropOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px",
                textAlign: "left", border: "none",
                background: viewScope === "mine" ? "#f0fdf4" : "#fff",
                fontWeight: viewScope === "mine" ? 600 : 400,
                fontSize: 13, cursor: "pointer", color: "#374151",
              }}
            >
              <FaUser size={10} style={{ color: "#6b7280", flexShrink: 0 }} /> My Leads
            </button>
            <div style={{ height: 1, background: "#e5e7eb" }} />
            <button
              onClick={() => { setViewScope("agency"); setAgentFilter(null); setDropOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px",
                textAlign: "left", border: "none",
                background: viewScope === "agency" && !agentFilter ? "#f0fdf4" : "#fff",
                fontWeight: viewScope === "agency" && !agentFilter ? 600 : 400,
                fontSize: 13, cursor: "pointer", color: "#374151",
              }}
            >
              <FaUsers size={11} style={{ color: "#6b7280", flexShrink: 0 }} /> All Team
            </button>
            {otherAgents.map((agent) => (
              <button
                key={agent.landing_page_slug}
                onClick={() => { setViewScope("agency"); setAgentFilter(agent.landing_page_slug); setDropOpen(false); }}
                style={{
                  display: "block", width: "100%", padding: "9px 14px 9px 33px", textAlign: "left",
                  border: "none",
                  background: agentFilter === agent.landing_page_slug ? "#f0fdf4" : "#fff",
                  fontWeight: agentFilter === agent.landing_page_slug ? 600 : 400,
                  fontSize: 13, cursor: "pointer", color: "#374151",
                }}
              >
                {agent.agent_name || agent.agent_email}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  function SidebarContent({ onLinkClick }) {
    const navScrollRef = useRef(null);
    const [showScrollHint, setShowScrollHint] = useState(true);
    const [showScrollTopHint, setShowScrollTopHint] = useState(false);

    useEffect(() => {
      const el = navScrollRef.current;
      if (!el) return;
      function checkScroll() {
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
        setShowScrollHint(!atBottom);
        setShowScrollTopHint(el.scrollTop > 20);
      }
      checkScroll();
      el.addEventListener("scroll", checkScroll, { passive: true });
      return () => el.removeEventListener("scroll", checkScroll);
    }, []);

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, position: "relative" }}>
        {/* Top row: Agency name and hamburger or X */}
        {!isDesktop && mobileNavOpen ? (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "8px 0 0 8px",
            minHeight: 56,
            gap: 8,
            flexShrink: 0,
            position: "relative"
          }}>
            <Link
              to="/"
              data-event-tab="main_nav"
              data-event-field="home_logo"
              data-event-action="navigate"
              style={{
                textDecoration: "none",
                marginLeft: '8px',
                display: "inline-flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <GradientHouseIcon width="30" height="30" />
              <VendorleadsGradientText style={{ fontSize: "1.5rem", cursor: "pointer" }} />
            </Link>
            {/* X close button in top right */}
            <CloseBtn
              onClick={() => setMobileNavOpen(false)}
              color="#b2dfdb"
              style={{ position: "absolute", right: 12, top: 12 }}
            />
          </div>
        ) : isDesktop ? (
          <div style={{
            padding: 20,
            display: "flex",
            alignItems: "center",
            gap: 10,
            minHeight: 56,
            flexShrink: 0,
          }}>
            <Link
              to="/"
              data-event-tab="main_nav"
              data-event-field="home_logo"
              data-event-action="navigate"
              style={{
                textDecoration: "none",
                marginLeft: '2px',
                display: "inline-flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <GradientHouseIcon width="30" height="30" />
              <VendorleadsGradientText style={{ fontSize: "1.5rem", cursor: "pointer" }} />
            </Link>      
          </div>
        ) : null}
        {/* Main nav content */}
        <div ref={navScrollRef} style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          position: "relative",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}>
          {showScrollTopHint && (
            <div style={{
              position: "sticky", top: 0, left: 0, right: 0,
              height: 40, pointerEvents: "none", flexShrink: 0,
              background: "linear-gradient(to top, rgba(15,60,63,0), rgba(15,60,63,0.95))",
              display: "flex", alignItems: "flex-start", justifyContent: "center",
              paddingTop: 6, zIndex: 2,
            }}>
              <FaChevronUp style={{ fontSize: 10, color: "#7fb8b0", animation: "navScrollBounceUp 1.5s ease-in-out infinite" }} />
              <style>{`@keyframes navScrollBounceUp { 0%, 100% { transform: translateY(0); opacity: 0.6; } 50% { transform: translateY(-3px); opacity: 1; } }`}</style>
            </div>
          )}
            <nav style={{ marginTop: showScrollTopHint ? 0 : 30 }}>
                {!hasContacts && (
                  <div
                    onClick={() => setShowUploadModal(true)}
                    data-track-click="true"
                    data-event-tab="main_nav"
                    data-event-field="upload_contacts_top"
                    data-event-action="open_modal"
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 24px", borderRadius: 8,
                      background: "linear-gradient(90deg, #0d9488, #0ea5e9)",
                      color: "#fff", cursor: "pointer", margin: "6px 12px",
                      fontSize: 14, fontWeight: 600,
                      transition: "opacity 0.15s",
                    }}
                  >
                    <FaCloudUploadAlt style={{ fontSize: 16, flexShrink: 0 }} />
                    <span>Upload Contacts</span>
                  </div>
                )}
                <SidebarLink
                    icon={<FaRocket />}
                    label="Getting Started"
                    to="/getting-started"
                    active={location.pathname === "/getting-started"}
                    onClick={() => { onLinkClick(); navigate("/getting-started"); }}
                />
                <SidebarLink
                    icon={<FaChartBar />}
                    label="Dashboard"
                    to="/dashboard"
                    active={location.pathname === "/dashboard"}
                    onClick={() => { onLinkClick(); navigate("/dashboard"); }}
                />
                <SidebarLink
                    icon={<FaAddressBook />}
                    label="Contacts"
                    to="/contacts"
                    active={location.pathname === "/contacts"}
                    onClick={() => { onLinkClick(); navigate("/contacts"); }}
                />
                <SidebarLink
                    icon={<FaHome />}
                    label="Properties"
                    to="/properties"
                    active={location.pathname === "/properties"}
                    onClick={() => { onLinkClick(); navigate("/properties"); }}
                />
                <SidebarSection label="AI Agent">
                    <SidebarLink
                    icon={<FaClipboardCheck />}
                    label="Needs Review"
                    to="/needs-review"
                    active={location.pathname === "/needs-review"}
                    onClick={() => { onLinkClick(); navigate("/needs-review"); }}
                    badge={pendingReviewCount}
                    />
                    <SidebarLink
                    icon={<FaInbox />}
                    label="AI Inbox"
                    to="/ai-inbox"
                    active={
                        location.pathname === "/ai-inbox" ||
                        location.pathname === "/ai-conversations" ||
                        location.pathname === "/ai-outbound-messages"
                    }
                    onClick={() => { onLinkClick(); navigate("/ai-inbox"); }}
                    />
                    <SidebarLink
                    icon={<FaCommentDots />}
                    label="Agent Chat"
                    to="/ai-assistant"
                    active={location.pathname === "/ai-assistant"}
                    onClick={() => { onLinkClick(); navigate("/ai-assistant"); }}
                    />
                </SidebarSection>
                <SidebarSection label="Leads">
                    <SidebarLink
                    icon={<FaSearch />}
                    label="Property Searches"
                    to="/leads-property-searches"
                    active={location.pathname === "/leads-property-searches"}
                    onClick={() => { onLinkClick(); navigate("/leads-property-searches"); }}
                    />
                    <SidebarLink
                    icon={<FaBell />}
                    label="Notification History"
                    to="/leads-past-notifications"
                    active={location.pathname === "/leads-past-notifications"}
                    onClick={() => { onLinkClick(); navigate("/leads-past-notifications"); }}
                    />
                    <SidebarLink
                    icon={<FaHistory />}
                    label="Activity Explorer"
                    to="/user-activity"
                    active={location.pathname === "/user-activity"}
                    onClick={() => { onLinkClick(); navigate("/user-activity"); }}
                    />
                    <SidebarLink
                    icon={<FaMapMarkedAlt />}
                    label="Map Prospector"
                    to="/map-prospector"
                    active={location.pathname === "/map-prospector"}
                    onClick={() => { onLinkClick(); navigate("/map-prospector"); }}
                    />
                </SidebarSection>
                <SidebarSection label="Tools">
                    <SidebarLink
                    icon={<FaRegNewspaper />}
                    label="AI Articles"
                    to="/ai-news-articles"
                    active={location.pathname === "/ai-news-articles"}
                    onClick={() => { onLinkClick(); navigate("/ai-news-articles"); }}
                    />
                    <SidebarLink
                    icon={<FaEnvelopeOpenText />}
                    label="AI Email Campaigns"
                    to="/ai-email-campaigns"
                    active={location.pathname === "/ai-email-campaigns"}
                    onClick={() => { onLinkClick(); navigate("/ai-email-campaigns"); }}
                    />
                    {/* AI Video tab hidden
                    <SidebarLink
                    icon={<FaVideo />}
                    label="AI Video"
                    to="/ai-video"
                    active={location.pathname === "/ai-video"}
                    onClick={() => { onLinkClick(); navigate("/ai-video"); }}
                    />
                    */}
                    <SidebarLink
                    icon={<FaBullhorn />}
                    label="AI Infographics"
                    to="/campaign-digital-ads"
                    active={location.pathname === "/campaign-digital-ads"}
                    onClick={() => { onLinkClick(); navigate("/campaign-digital-ads"); }}
                    />
                    <SidebarLink
                    icon={<FaHome />}
                    label="Appraisals"
                    to="/leads-property-appraisals"
                    active={location.pathname === "/leads-property-appraisals"}
                    onClick={() => { onLinkClick(); navigate("/leads-property-appraisals"); }}
                    />
                    <SidebarLink
                    icon={<FaQrcode />}
                    label="QR Code"
                    to="/campaign-qr-code"
                    active={location.pathname === "/campaign-qr-code"}
                    onClick={() => { onLinkClick(); navigate("/campaign-qr-code"); }}
                    />
                </SidebarSection>
                </nav>
          {/* Scroll hint fade for mobile */}
          {showScrollHint && (
            <div style={{
              position: "sticky", bottom: 0, left: 0, right: 0,
              height: 40, pointerEvents: "none", flexShrink: 0,
              background: "linear-gradient(to bottom, rgba(15,60,63,0), rgba(15,60,63,0.95))",
              display: "flex", alignItems: "flex-end", justifyContent: "center",
              paddingBottom: 6,
            }}>
              <FaChevronDown style={{ fontSize: 10, color: "#7fb8b0", animation: "navScrollBounce 1.5s ease-in-out infinite" }} />
              <style>{`@keyframes navScrollBounce { 0%, 100% { transform: translateY(0); opacity: 0.6; } 50% { transform: translateY(3px); opacity: 1; } }`}</style>
            </div>
          )}
        </div>
        <hr style={{ margin: 0, flexShrink: 0 }} />
        {/* View scope toggle + Account links at bottom */}
        <div style={{ flexShrink: 0, padding: "16px 20px 20px 20px", boxSizing: "border-box" }}>
          <ScopeToggle />
        </div>
        <div style={{
          flexShrink: 0,
          // Add safe-area inset on mobile so the bottom items (Settings,
          // Billing, Send Feedback) clear the browser's bottom toolbar /
          // home indicator on real devices.
          paddingBottom: isDesktop ? 12 : "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        }}>
          <div style={{ fontSize: 11, color: "#b2dfdb", margin: "0 0 4px 24px" }}>Account</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 24px 8px", color: "#b2dfdb" }}>
            <FaUserCircle style={{ fontSize: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userEmail}</span>
          </div>
          <div
            onClick={() => { onLinkClick(); navigate("/settings"); }}
            data-track-click="true"
            data-event-tab="main_nav"
            data-event-field="settings"
            data-event-action="navigate"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 24px",
              background: location.pathname === "/settings" ? "#17403b" : "none",
              borderRadius: 8,
              fontWeight: location.pathname === "/settings" ? 500 : 300,
              color: location.pathname === "/settings" ? "#fff" : "#b2dfdb",
              cursor: "pointer",
              marginBottom: 2,
              fontSize: 14,
              width: "100%",
              boxSizing: "border-box",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            <FaCog style={{ fontSize: 14 }} />
            <span>Settings</span>
          </div>
          <div
            onClick={() => { onLinkClick(); navigate("/billing"); }}
            data-track-click="true"
            data-event-tab="main_nav"
            data-event-field="billing"
            data-event-action="navigate"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 24px",
              background: location.pathname === "/billing" ? "#17403b" : "none",
              borderRadius: 8,
              fontWeight: location.pathname === "/billing" ? 500 : 300,
              color: location.pathname === "/billing" ? "#fff" : "#b2dfdb",
              cursor: "pointer",
              marginBottom: 2,
              fontSize: 14,
              width: "100%",
              boxSizing: "border-box",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            <FaCreditCard style={{ fontSize: 14 }} />
            <span>Billing</span>
          </div>
          {hasContacts && (
          <div
            onClick={() => setShowUploadModal(true)}
            data-track-click="true"
            data-event-tab="main_nav"
            data-event-field="upload_contacts"
            data-event-action="open_modal"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 24px",
              borderRadius: 8,
              fontWeight: 300,
              color: "#b2dfdb",
              cursor: "pointer",
              marginBottom: 2,
              fontSize: 14,
              width: "100%",
              boxSizing: "border-box",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            <FaCloudUploadAlt style={{ fontSize: 14 }} />
            <span>Upload Contacts</span>
          </div>
          )}
          <div
            onClick={() => { onLinkClick(); window.dispatchEvent(new Event('open-feedback-widget')); }}
            data-track-click="true"
            data-event-tab="main_nav"
            data-event-field="feedback"
            data-event-action="open_modal"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 24px",
              borderRadius: 8,
              fontWeight: 300,
              color: "#b2dfdb",
              cursor: "pointer",
              marginBottom: 2,
              fontSize: 14,
              width: "100%",
              boxSizing: "border-box",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            <FaCommentDots style={{ fontSize: 14 }} />
            <span>Send Feedback</span>
          </div>
        </div>
      </div>
    );
  }

  function MobileTopBar() {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: 56,
        background: "#fff",
        zIndex: 350,
        display: "flex",
        alignItems: "center",
        boxShadow: "0 1px 4px #0001",
        borderBottom: "1px solid #eee",
      }}>
        {!mobileNavOpen && (
          <HamburgerBtn
            onClick={() => setMobileNavOpen(true)}
            style={{
              marginLeft: 8,
            }}
            color="#17403b"
          />
        )}
      </div>
    );
  }

  return (
    <>
      {/* Mobile: static top bar with hamburger when nav is closed */}
      {!isDesktop && <MobileTopBar />}

      {/* Overlay for mobile nav */}
      {!isDesktop && <div style={overlayStyle} onClick={() => setMobileNavOpen(false)} />}

      {/* Sidebar */}
      <aside ref={sidebarRef} style={sidebarStyle}>
        <SidebarContent onLinkClick={() => { if (!isDesktop) setMobileNavOpen(false); }} />
      </aside>
      {/* Add padding to main content if desktop */}
      {isDesktop && <div style={{ width: 260, flexShrink: 0 }} />}
      <UploadContactsModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
    </>
  );
}

function SidebarLink({ icon, label, active, onClick, badge }) {
    const fieldKey = (label || 'nav_link').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const hasBadge = typeof badge === 'number' && badge > 0;
    return (
      <div
        onClick={onClick}
        data-track-click="true"
        data-event-tab="main_nav"
        data-event-field={fieldKey || 'nav_link'}
        data-event-action="navigate"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 24px",
          background: active ? "#17403b" : "none",
          borderRadius: 8,
          fontWeight: active ? 500 : 300,
          color: active ? "#fff" : "#b2dfdb",
          cursor: "pointer",
          marginBottom: 2,
          fontSize: 14,
          width: "100%",
          boxSizing: "border-box",
          transition: "background 0.15s, color 0.15s",
        }}
      >
        <span style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          minWidth: 24,
          height: 16,
        }}>
          {icon}
        </span>
        <span style={{ marginLeft: 8, flex: 1 }}>{label}</span>
        {hasBadge && (
          <span style={{
            background: "#7c3aed",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
            minWidth: 20,
            textAlign: "center",
            lineHeight: 1.3,
          }}>
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
    );
  }

function SidebarSection({ label, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: '11px', color: "#b2dfdb", margin: "8px 0 4px 24px" }}>{label}</div>
      {children}
    </div>
  );
}